import { useCallback, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Stack, Tooltip, Typography } from '@mui/material';

import { createNewOrderStore } from 'stores/createNewOrderStore';
import { useLocalization } from 'localization';
import { getSaleProductsApi } from 'api/products/query';
import { ProductStructure } from 'constants/enums/products';
import { GridColDef, GridRenderCellParams, GridRowSelectionModel } from '@mui/x-data-grid';
import { IProductDto } from 'api/products/dto';
import { CurrencyCode } from 'constants/enums/general';
import useNewOrderCalculatePrice from 'hooks/general/useNewOrderCalculatePrice';
import { userStore } from 'stores/userStore';

export const useCreateNewOrderStep3 = () => {
  const { t, f } = useLocalization();
  const { nextStep, formData, setFormData } = createNewOrderStore();
  const { getValues, setValue, watch } = useFormContext();
  const { user } = userStore();

  const { data, isLoading } = getSaleProductsApi({
    branchId: formData?.branchId,
    pageNumber: 2,
  });

  const groupedPackages = useMemo(() => {
    if (!data) return {};

    const bundles = data.filter(x => x.productStructure === ProductStructure.BundleProduct);

    const result: Record<
      string,
      {
        name: string; // displayCategory
        rows: IProductDto[]; // all items in this group
        order: number; //sort by displayCategoryOrder
      }
    > = {};

    for (const item of bundles) {
      const id = item.categoryId ?? 'Unknown';

      if (!result[id]) {
        result[id] = {
          name: item.displayCategory ?? 'Unknown',
          rows: [],
          order: item.displayCategoryOrder ?? 9999,
        };
      }

      result[id].rows.push(item);
    }

    // Sort rows alphabetically (case-insensitive)
    for (const key of Object.keys(result)) {
      result[key].rows.sort((a, b) => {
        const nameA = a.name?.toLowerCase() ?? '';
        const nameB = b.name?.toLowerCase() ?? '';
        return nameA.localeCompare(nameB);
      });
    }

    // Sort groups by displayCategoryOrder
    return Object.fromEntries(Object.entries(result).sort(([, a], [, b]) => (a.order ?? 9999) - (b.order ?? 9999)));
  }, [data]);

  const handleSelectionChange = useCallback(
    (categoryId: string, allRows: IProductDto[]) => (selection: GridRowSelectionModel) => {
      const ids = Array.from(selection.ids);
      const lastSelectedId = ids[ids.length - 1];
      const selectedRow = allRows.find(r => r.id === lastSelectedId);
      setValue(`bundlePackagesProduct.${categoryId}`, selectedRow ? [selectedRow] : [], { shouldValidate: false });
    },
    [setValue]
  );

  const handleRowSelectionModelChange = useCallback(
    (categoryId: string): GridRowSelectionModel => {
      const selected: IProductDto[] = watch(`bundlePackagesProduct.${categoryId}`) || [];
      const id = selected[0]?.id;
      return {
        type: 'include',
        ids: id ? new Set([id]) : new Set(),
      };
    },
    [watch]
  );

  const onNext = useCallback(() => {
    const finalGroups = Object.keys(groupedPackages).reduce(
      (acc, categoryId) => {
        acc[categoryId] = getValues(`bundlePackagesProduct.${categoryId}`) || [];
        return acc;
      },
      {} as Record<string, IProductDto[]>
    );

    setFormData({
      ...formData,
      bundlePackagesProduct: finalGroups,
    });

    nextStep();
  }, [formData, nextStep, getValues, groupedPackages, setFormData]);

  const totalPrice = useNewOrderCalculatePrice();

  const columns = useMemo((): GridColDef<IProductDto>[] => {
    const columns: GridColDef<IProductDto>[] = [
      {
        headerName: t('orders.pages.new.flowSelection.packageName'),
        field: 'name',
        minWidth: 300,
        sortable: false,
        flex: 1,
      },
      {
        headerName: t('common.terms.products'),
        field: 'children',
        width: 300,
        sortable: false,
        renderCell: (params: GridRenderCellParams<IProductDto, IProductDto['children']>) => {
          const { value } = params;
          return (
            <Stack
              textAlign="center"
              alignItems="center"
              direction="row"
              height={'100%'}
              sx={{ display: value && value?.length > 0 ? 'initial' : 'none' }}
            >
              <Tooltip
                title={
                  <Stack direction={'column'} spacing={1}>
                    {value?.map((product, index) => {
                      return (
                        <Stack key={`${product?.name}${index}`} direction="row" width="100%" gap={2}>
                          <Typography variant="body2" color="white" sx={{ minWidth: '2rem' }}>
                            {product?.quantity}
                          </Typography>
                          <Typography variant="body2" color="white" sx={{ minWidth: '2rem' }}>
                            x
                          </Typography>
                          <Typography variant="body2" color="white">
                            {product?.name}
                          </Typography>
                        </Stack>
                      );
                    })}
                  </Stack>
                }
              >
                <InfoOutlinedIcon sx={{ fontSize: '1.2rem', color: 'gray' }} />
              </Tooltip>
            </Stack>
          );
        },
      },
    ];

    if (user?.visiblePrices) {
      columns.push({
        headerName: t('common.terms.price'),
        field: 'discountedPrice',
        minWidth: 300,
        sortable: false,
        renderCell: (params: GridRenderCellParams<IProductDto, IProductDto['discountedPrice']>) => {
          const { value } = params;
          return value ? f(value || 0, { currency: CurrencyCode.DKK, decimals: 2 }) : '-';
        },
      });
    }

    return columns;
  }, [t, f, user?.visiblePrices]);

  return {
    t,
    ui: { table: { columns } },
    isLoading,
    groupedPackages,
    handleSelectionChange,
    handleRowSelectionModelChange,
    onNext,
    totalPrice,
    apiState: { showPrice: user?.visiblePrices },
  };
};
